/**
 * チバリヨ - S5 KPI画面 (Phase 2-B-3.2)
 *
 * 「今月の自分のKPI達成状況を詳しく確認する」画面。
 *
 * セクション構成:
 *   1. ヘッダー: 対象年月 + ペースバッジ + 再読み込みボタン
 *   2. サマリーカード: アポ獲得・見積提出・訪問件数 (目標 / 実績 / 達成率 / プログレスバー)
 *   3. ペース詳細: ペースpt・営業日進捗・「このペースだと月末 XX 件」予測
 *   4. 当期進捗: 期首〜当月の累計 + 月別振り返り (kpi-fiscal.js 共有モジュール)
 *   5. 非KPI対象 (粟村・松尾佳) → チーム合算表示 (S1と同じ分岐)
 *
 * ロジック: S1のペース計算をそのまま再利用
 * クエリ: reports/goals は当期範囲の単一フィールド範囲クエリ (追加インデックス不要)。
 *         当月分はそこからクライアント側で切り出す。
 *
 * 使い方:
 *   const ctrl = mountS5Kpi(rootEl, { currentMember, showToast });
 *   ctrl.onShow({ reload: true });
 *   ctrl.destroy();
 */

import {
  collection,
  getDocs,
  doc,
  getDoc,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';
import { db } from './firebase-init.js';
import {
  resolveFiscalPeriod,
  fetchFiscalKpiData,
  buildFiscalRows,
  renderFiscalContentHtml,
  renderFiscalTableHtml,
  fiscalMemberIdSet,
  formatYmJa,
} from './kpi-fiscal.js';

// ─── ユーティリティ ───────────────────────────────────────────
function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatYearMonth(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function formatYearMonthJa(ym) {
  // "2026-06" → "2026年6月"
  const [y, m] = ym.split('-');
  return `${y}年${Number(m)}月`;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// ─── 公開関数 ─────────────────────────────────────────────────
export function mountS5Kpi(rootEl, opts) {
  const { currentMember } = opts || {};

  const state = {
    loading: false,
    destroyed: false,
    config: null,
    members: [],
    goalsMap: new Map(),
    monthReports: [],
    holidaySet: new Set(),
    // 当期 (kpi-fiscal.js)
    period: null,
    fiscalReports: [],
    goalsByMonth: new Map(),
  };

  rootEl.innerHTML = `
    <div class="s5k-root">
      <div class="s5k-page-header">
        <div>
          <h2 class="s5k-page-title">KPI</h2>
          <div class="s5k-page-date" id="s5k-date"></div>
        </div>
        <button type="button" class="s5k-refresh" id="s5k-refresh" aria-label="再読み込み">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"/>
          </svg>
        </button>
      </div>
      <div class="s5k-error" id="s5k-error" hidden></div>
      <div class="s5k-body" id="s5k-body">
        <div class="s5k-loading">読み込み中…</div>
      </div>
    </div>
  `;

  const bodyEl = rootEl.querySelector('#s5k-body');
  const errorEl = rootEl.querySelector('#s5k-error');
  rootEl.querySelector('#s5k-refresh').addEventListener('click', () => reload());

  // 年月表示
  const dateEl = rootEl.querySelector('#s5k-date');
  const now0 = new Date();
  dateEl.textContent = `${now0.getFullYear()}年${now0.getMonth() + 1}月`;

  // ─── データ取得 ─────────────────────────────────────────────
  async function fetchAll() {
    const now = new Date();
    const yearMonth = formatYearMonth(now);

    // config を先に読んで当期範囲を確定してから、reports/goals を期間クエリで一括取得
    const [cSnap, mSnap, hSnap] = await Promise.all([
      getDoc(doc(db, 'config', 'system')),
      getDocs(collection(db, 'members')),
      getDocs(collection(db, 'holidays')),
    ]);

    if (!cSnap.exists()) throw new Error('config/system が存在しません');
    state.config = cSnap.data();

    state.members = mSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((m) => m.active !== false && m.chibariyoAccess !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    state.holidaySet = new Set();
    for (const d of hSnap.docs) {
      const h = d.data();
      const s = h.startDate;
      const e = h.endDate || h.startDate;
      if (!s) continue;
      const startD = new Date(s);
      const endD = new Date(e);
      for (let d2 = new Date(startD); d2 <= endD; d2.setDate(d2.getDate() + 1)) {
        state.holidaySet.add(formatDate(d2));
      }
    }

    state.period = resolveFiscalPeriod(state.config, now);
    const { reports, goalsByMonth } = await fetchFiscalKpiData(db, state.period);
    state.fiscalReports = reports;
    state.goalsByMonth = goalsByMonth;

    // 当月分は当期データから切り出す (従来の当月クエリと同じ結果になる)
    state.monthReports = reports.filter((r) => r.date && r.date.startsWith(yearMonth));
    state.goalsMap = new Map(goalsByMonth.get(yearMonth) || []);
  }

  // ─── ロジック (S1と同一) ────────────────────────────────────
  function isBusinessDay(d) {
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return false;
    if (state.holidaySet.has(formatDate(d))) return false;
    return true;
  }

  function computeBusinessDayProgress() {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    let elapsed = 0;
    let total = 0;
    for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
      if (isBusinessDay(d)) {
        total += 1;
        if (d <= now) elapsed += 1;
      }
    }
    return { elapsed, total };
  }

  function memberActuals(memberId) {
    const my = state.monthReports.filter((r) => r.memberId === memberId);
    return {
      appointment: my.filter((r) => r.type === '訪問' && r.hasAppointment === true).length,
      quote: my.filter((r) => r.quote === true).length,
      visit: my.filter((r) => r.type === '訪問').length,
      reportCount: my.length,
    };
  }

  function computePacePts(actuals, goal, progress) {
    const expectedRatio = progress.total > 0 ? progress.elapsed / progress.total : 0;
    const apptPacePt = goal.appointmentTarget > 0
      ? ((actuals.appointment / goal.appointmentTarget) - expectedRatio) * 100
      : null;
    const quotePacePt = goal.quoteTarget > 0
      ? ((actuals.quote / goal.quoteTarget) - expectedRatio) * 100
      : null;
    let minPt = Infinity;
    if (apptPacePt !== null) minPt = Math.min(minPt, apptPacePt);
    if (quotePacePt !== null) minPt = Math.min(minPt, quotePacePt);
    if (minPt === Infinity) minPt = null;
    return { apptPacePt, quotePacePt, minPt };
  }

  function paceStatus(pt) {
    const danger = state.config.alertPacePtThreshold ?? -15;
    const warn = state.config.paceThresholdGood ?? -5;
    if (pt >= warn) return 'good';
    if (pt >= danger) return 'warn';
    return 'danger';
  }

  function paceLabel(status) {
    return { good: '順調', warn: '注意', danger: '危険' }[status] || '—';
  }

  function formatRate(actual, target) {
    if (!target) return '—';
    return `${Math.round(actual / target * 100)}%`;
  }

  function formatPt(pt) {
    if (pt === null || pt === undefined) return '—';
    return `${pt >= 0 ? '+' : ''}${pt.toFixed(1)}pt`;
  }

  /** 月末着地予測: actual / elapsed * total (小数点以下切り捨て) */
  function forecastMonthEnd(actual, progress) {
    if (progress.elapsed === 0) return '—';
    return String(Math.floor(actual / progress.elapsed * progress.total));
  }

  function renderProgressBar(actual, target) {
    if (!target) return '<div class="s5k-bar"><div class="s5k-bar-fill is-empty"></div></div>';
    const pct = Math.min(100, Math.round(actual / target * 100));
    let cls = 'is-low';
    if (pct >= 100) cls = 'is-complete';
    else if (pct >= 75) cls = 'is-high';
    else if (pct >= 50) cls = 'is-mid';
    return `<div class="s5k-bar"><div class="s5k-bar-fill ${cls}" style="width:${pct}%"></div></div>`;
  }

  // ─── 描画 ──────────────────────────────────────────────────
  function render() {
    const yearMonth = formatYearMonth(new Date());
    const progress = computeBusinessDayProgress();
    const isKpiTarget = state.goalsMap.has(currentMember.memberId);

    if (isKpiTarget) {
      renderKpiView(yearMonth, progress);
    } else {
      renderTeamView(yearMonth, progress);
    }
  }

  function renderKpiView(yearMonth, progress) {
    const me = currentMember.memberId;
    const actuals = memberActuals(me);
    const goal = state.goalsMap.get(me);
    const pace = computePacePts(actuals, goal, progress);
    const status = pace.minPt !== null ? paceStatus(pace.minPt) : null;

    bodyEl.innerHTML = `
      ${renderSummaryCard(formatYearMonthJa(yearMonth), actuals, goal, status, progress, pace)}
      ${renderPaceDetail(pace, actuals, goal, progress)}
      ${renderBusinessDayCard(progress)}
      ${renderFiscalSection(me)}
    `;
  }

  function renderTeamView(yearMonth, progress) {
    // KPI対象メンバーの合算
    const kpiMemberIds = [...state.goalsMap.keys()];
    const totals = { appointment: 0, quote: 0, visit: 0, reportCount: 0 };
    const goalTotals = { appointmentTarget: 0, quoteTarget: 0 };
    for (const id of kpiMemberIds) {
      const a = memberActuals(id);
      totals.appointment += a.appointment;
      totals.quote += a.quote;
      totals.visit += a.visit;
      totals.reportCount += a.reportCount;
      const g = state.goalsMap.get(id);
      goalTotals.appointmentTarget += g.appointmentTarget;
      goalTotals.quoteTarget += g.quoteTarget;
    }
    const pace = computePacePts(totals, goalTotals, progress);
    const status = pace.minPt !== null ? paceStatus(pace.minPt) : null;

    bodyEl.innerHTML = `
      <div class="s5k-team-note">チーム全体の進捗を表示しています</div>
      ${renderSummaryCard(`チーム進捗 ${formatYearMonthJa(yearMonth)}`, totals, goalTotals, status, progress, pace)}
      ${renderPaceDetail(pace, totals, goalTotals, progress)}
      ${renderBusinessDayCard(progress)}
      ${renderFiscalSection(null)}
      ${renderMemberCards(progress)}
    `;
  }

  /** 当期進捗 (memberId 指定で個人、null でチーム合算) */
  function renderFiscalSection(memberId) {
    const p = state.period;
    if (!p || p.months.length === 0) return '';
    const fiscal = buildFiscalRows({
      reports: state.fiscalReports,
      goalsByMonth: state.goalsByMonth,
      months: p.months,
      memberId,
    });
    const label = p.fiscalYear ? `第${p.fiscalYear}期` : '当期';
    return `
      <section class="s5k-section">
        <div class="s5k-section-header">
          <h3 class="s5k-section-title">当期進捗</h3>
          <span class="s5k-fiscal-range">${label} ${formatYmJa(p.startDate.slice(0, 7))}〜${formatYmJa(p.endDate.slice(0, 7))}</span>
        </div>
        <div class="s5k-card">
          ${renderFiscalContentHtml(fiscal, p.currentYm)}
        </div>
        ${memberId === null ? renderFiscalMemberCards() : ''}
      </section>
    `;
  }

  /** チーム表示用: 当期のメンバー別振り返りカード (期内に goals があるメンバーが対象) */
  function renderFiscalMemberCards() {
    const p = state.period;
    const targetIds = fiscalMemberIdSet(state.goalsByMonth);
    const cards = state.members
      .filter((m) => targetIds.has(m.id))
      .map((m) => {
        const f = buildFiscalRows({
          reports: state.fiscalReports,
          goalsByMonth: state.goalsByMonth,
          months: p.months,
          memberId: m.id,
        });
        return `
          <div class="s5k-member-card">
            <div class="s5k-member-header">
              <span class="s5k-member-name">${escapeHtml(m.name)}</span>
            </div>
            ${renderFiscalTableHtml(f, p.currentYm)}
          </div>
        `;
      }).join('');
    if (!cards) return '';
    return `<div class="s5k-fiscal-members">${cards}</div>`;
  }

  function renderSummaryCard(title, actuals, goal, status, progress, pace) {
    return `
      <section class="s5k-section">
        <div class="s5k-section-header">
          <h3 class="s5k-section-title">${escapeHtml(title)}</h3>
          ${status ? `<span class="s5k-pace-badge is-pace-${status}">${paceLabel(status)}</span>` : ''}
        </div>
        <div class="s5k-card">
          ${renderMetric('アポ獲得', actuals.appointment, goal.appointmentTarget, pace.apptPacePt)}
          ${renderMetric('見積提出', actuals.quote, goal.quoteTarget, pace.quotePacePt)}
          <div class="s5k-metric s5k-metric-visit">
            <div class="s5k-metric-top">
              <span class="s5k-metric-label">訪問件数</span>
              <span class="s5k-metric-value">${actuals.visit} <span class="s5k-metric-unit">件</span></span>
            </div>
          </div>
          <div class="s5k-metric s5k-metric-visit">
            <div class="s5k-metric-top">
              <span class="s5k-metric-label">日報総数</span>
              <span class="s5k-metric-value">${actuals.reportCount} <span class="s5k-metric-unit">件</span></span>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  function renderMetric(label, actual, target, pacePt) {
    const ptHtml = pacePt !== null
      ? `<span class="s5k-metric-pt ${pacePt >= 0 ? 'is-positive' : 'is-negative'}">${formatPt(pacePt)}</span>`
      : '';
    return `
      <div class="s5k-metric">
        <div class="s5k-metric-top">
          <span class="s5k-metric-label">${escapeHtml(label)}</span>
          <span class="s5k-metric-value">${actual} <span class="s5k-metric-target">/ ${target}</span></span>
          <span class="s5k-metric-rate">${formatRate(actual, target)}</span>
          ${ptHtml}
        </div>
        ${renderProgressBar(actual, target)}
      </div>
    `;
  }

  function renderPaceDetail(pace, actuals, goal, progress) {
    if (pace.minPt === null) return '';
    const apptForecast = goal.appointmentTarget > 0
      ? `<span>月末着地予測: <strong>${forecastMonthEnd(actuals.appointment, progress)}件</strong> / 目標${goal.appointmentTarget}件</span>`
      : '';
    const quoteForecast = goal.quoteTarget > 0
      ? `<span>月末着地予測: <strong>${forecastMonthEnd(actuals.quote, progress)}件</strong> / 目標${goal.quoteTarget}件</span>`
      : '';

    return `
      <section class="s5k-section">
        <div class="s5k-section-header">
          <h3 class="s5k-section-title">ペース詳細</h3>
        </div>
        <div class="s5k-card s5k-pace-card">
          <div class="s5k-pace-row">
            <span class="s5k-pace-label">アポ ペース</span>
            <span class="s5k-pace-val ${pace.apptPacePt !== null && pace.apptPacePt >= 0 ? 'is-positive' : 'is-negative'}">${formatPt(pace.apptPacePt)}</span>
            ${apptForecast}
          </div>
          <div class="s5k-pace-row">
            <span class="s5k-pace-label">見積 ペース</span>
            <span class="s5k-pace-val ${pace.quotePacePt !== null && pace.quotePacePt >= 0 ? 'is-positive' : 'is-negative'}">${formatPt(pace.quotePacePt)}</span>
            ${quoteForecast}
          </div>
        </div>
      </section>
    `;
  }

  function renderBusinessDayCard(progress) {
    const pct = progress.total > 0 ? Math.round(progress.elapsed / progress.total * 100) : 0;
    return `
      <section class="s5k-section">
        <div class="s5k-section-header">
          <h3 class="s5k-section-title">営業日進捗</h3>
        </div>
        <div class="s5k-card s5k-bizday-card">
          <div class="s5k-bizday-nums">
            <span class="s5k-bizday-elapsed">${progress.elapsed}</span>
            <span class="s5k-bizday-sep"> / </span>
            <span class="s5k-bizday-total">${progress.total}</span>
            <span class="s5k-bizday-unit">日</span>
            <span class="s5k-bizday-pct">${pct}%経過</span>
          </div>
          <div class="s5k-bar">
            <div class="s5k-bar-fill is-bizday" style="width:${pct}%"></div>
          </div>
        </div>
      </section>
    `;
  }

  /** 非KPI対象用: メンバー別カード */
  function renderMemberCards(progress) {
    const kpiMembers = state.members.filter((m) => state.goalsMap.has(m.id));
    if (kpiMembers.length === 0) return '';

    const cards = kpiMembers.map((m) => {
      const actuals = memberActuals(m.id);
      const goal = state.goalsMap.get(m.id);
      const pace = computePacePts(actuals, goal, progress);
      const status = pace.minPt !== null ? paceStatus(pace.minPt) : null;
      return `
        <div class="s5k-member-card">
          <div class="s5k-member-header">
            <span class="s5k-member-name">${escapeHtml(m.name)}</span>
            ${status ? `<span class="s5k-pace-badge is-pace-${status}">${paceLabel(status)}</span>` : ''}
          </div>
          ${renderMetric('アポ獲得', actuals.appointment, goal.appointmentTarget, pace.apptPacePt)}
          ${renderMetric('見積提出', actuals.quote, goal.quoteTarget, pace.quotePacePt)}
          <div class="s5k-member-footer">訪問 ${actuals.visit}件 ・ 日報 ${actuals.reportCount}件</div>
        </div>
      `;
    }).join('');

    return `
      <section class="s5k-section">
        <div class="s5k-section-header">
          <h3 class="s5k-section-title">メンバー別</h3>
        </div>
        ${cards}
      </section>
    `;
  }

  // ─── リロード ──────────────────────────────────────────────
  async function reload() {
    if (state.loading || state.destroyed) return;
    state.loading = true;
    errorEl.hidden = true;
    bodyEl.innerHTML = '<div class="s5k-loading">読み込み中…</div>';
    try {
      await fetchAll();
      if (state.destroyed) return;
      render();
    } catch (e) {
      console.error('[S5] KPI読み込み失敗:', e);
      errorEl.textContent = 'KPIの読み込みに失敗しました: ' + (e.message || e);
      errorEl.hidden = false;
      bodyEl.innerHTML = '';
    } finally {
      state.loading = false;
    }
  }

  reload();

  return {
    onShow: (o = {}) => { if (o.reload) reload(); },
    destroy: () => { state.destroyed = true; },
  };
}
